/**
 * 消息 Popover 管理 Composable
 * 用于管理消息长按弹出的操作菜单（复制、删除、举报）
 */

import { ref, type CSSProperties } from 'vue'

export interface PopoverPosition {
  top: number
  left: number
}

export function useMessagePopover() {
  // 每条消息的 popover 状态（messageId -> 是否显示）
  const popoverStates = ref<Record<string, boolean>>({})
  
  // 每条消息的 popover 位置
  const popoverPositions = ref<Record<string, PopoverPosition>>({})

  // Popover 容器引用
  const popoverContainerRef = ref<HTMLElement | null>(null)

  /**
   * 打开指定消息的 Popover
   */
  const openPopover = (messageId: string, position?: { clientX: number; clientY: number }) => {
    // 关闭其他消息的 Popover
    closeAllPopovers()

    // 打开当前消息的 Popover
    popoverStates.value[messageId] = true

    // 计算并记录位置
    if (position) {
      const { clientX, clientY } = position
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const popW = 140 // 预计弹层宽度
      const popH = 60  // 预计弹层高度
      const margin = 8

      // 默认在手指上方显示，如果不足则放到下方
      let top = clientY - popH - margin
      if (top < margin) {
        top = Math.min(clientY + margin, viewportHeight - popH - margin)
      }

      let left = clientX - popW / 2
      if (left < margin) {
        left = margin
      }
      if (left + popW + margin > viewportWidth) {
        left = viewportWidth - popW - margin
      }

      popoverPositions.value[messageId] = { top, left }
    }
  }

  /**
   * 关闭指定消息的 Popover
   */
  const closePopover = (messageId: string) => {
    popoverStates.value[messageId] = false
  }

  /**
   * 关闭所有 Popover
   */
  const closeAllPopovers = () => {
    Object.keys(popoverStates.value).forEach(id => {
      popoverStates.value[id] = false
    })
  }

  /**
   * 获取 Popover 样式
   */
  const getPopoverStyle = (messageId: string): CSSProperties => {
    const pos = popoverPositions.value[messageId]
    if (!pos) return {} as CSSProperties

    return {
      position: 'fixed',
      top: pos.top + 'px',
      left: pos.left + 'px',
      zIndex: 99999
    }
  }

  /**
   * 设置 Popover 容器引用
   */
  const setPopoverRef = (value: any) => {
    popoverContainerRef.value = (value instanceof Element ? value as HTMLElement : null)
  }

  /**
   * 处理全局点击事件（点击外部关闭 Popover）
   */
  const handleGlobalClick = (event: Event) => {
    const target = event.target as Node
    const popContainer = popoverContainerRef.value

    // 点击弹层外部时，关闭所有弹层
    if (popContainer && popContainer instanceof HTMLElement && !popContainer.contains(target)) {
      closeAllPopovers()
    }
  }

  /**
   * 清理不存在的消息的状态
   */
  const cleanupStates = (existingMessageIds: Set<string>) => {
    Object.keys(popoverStates.value).forEach(messageId => {
      if (!existingMessageIds.has(messageId)) {
        delete popoverStates.value[messageId]
        delete popoverPositions.value[messageId]
      }
    })
  }

  /**
   * 注册全局事件监听器
   */
  const registerGlobalListeners = () => {
    document.addEventListener('touchstart', handleGlobalClick, true)
    document.addEventListener('click', handleGlobalClick, true)
  }

  /**
   * 移除全局事件监听器
   */
  const unregisterGlobalListeners = () => {
    document.removeEventListener('touchstart', handleGlobalClick, true)
    document.removeEventListener('click', handleGlobalClick, true)
  }

  return {
    popoverStates,
    popoverPositions,
    popoverContainerRef,
    openPopover,
    closePopover,
    closeAllPopovers,
    getPopoverStyle,
    setPopoverRef,
    cleanupStates,
    registerGlobalListeners,
    unregisterGlobalListeners
  }
}

